import React, { FC, useEffect, useMemo, useState } from "react";

import { LottieLoading } from "../../../../../../components/graphics/LottieLoading";
import CustomSelect from "../../../../../../components/select/Select";
import { Email } from "../../../../../../factories/emails/models/emails.models";
import { useAppSelector } from "../../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import {
  useGetEmailFoldersQuery,
  useGetRedirectUrlMutation,
  useLazyGetContactEmailsQuery,
} from "../../../../../../slices/EmailsSlice";
import {
  IOrganizationContact,
  SelectOptionType,
} from "../../../../../../types/types";
import {
  buildContactEmailFolderOptions,
  contactEmailsErrorState,
  getDefaultContactEmailFolderOption,
  getEmailRequestErrorText,
} from "../../../../components/emails/contactEmailFolders";
import {
  EmailConnectionEmptyState,
  EmailProviderModal,
} from "../../../../components/emails/ContactEmailsPageComponents";
import {
  ContactEmailsNoMessages,
  ContactEmailsThread,
} from "../../../../components/emails/ContactEmailsThread";
import { ComposeEmailModal } from "../../../../modals/ComposeEmailModal";

type Props = {
  organization: IOrganizationContact;
};

const getOrganizationPrimaryEmail = (organization: IOrganizationContact) => {
  const primaryEmail = organization?.emails?.find(
    (email) => email.isPrimaryEmail,
  );

  return (
    primaryEmail?.emailAddress || organization?.emails?.[0]?.emailAddress || ""
  );
};

export const OrganizationEmails: FC<Props> = ({ organization }) => {
  const { user } = useAppSelector((state) => state.appReducer);

  const [folder, setFolder] = useState<SelectOptionType | null>(null);
  const [emails, setEmails] = useState<Email[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [emailsError, setEmailsError] = useState("");
  const [isOpenCompose, setIsOpenCompose] = useState(false);
  const [actionEmail, setActionEmail] = useState<Email | null>(null);
  const [isOpenReply, setIsOpenReply] = useState(false);
  const [isOpenReplyAll, setIsOpenReplyAll] = useState(false);
  const [isForward, setIsForward] = useState(false);
  const [isOpenProvider, setIsOpenProvider] = useState(false);
  const [expandedEmailIds, setExpandedEmailIds] = useState<
    Record<string, boolean>
  >({});

  const [getContactEmails] = useLazyGetContactEmailsQuery();
  const contactEmail = getOrganizationPrimaryEmail(organization);
  const orgId = getLinkedFirm()?.orgId || "";

  const {
    data: folders,
    isLoading: isLoadingFolders,
    error: errorFolder,
  } = useGetEmailFoldersQuery({
    userId: user?.id || "",
  });

  const folderOptions = useMemo(
    () => buildContactEmailFolderOptions(folders),
    [folders],
  );
  const selectedFolderOption = useMemo(
    () =>
      folderOptions.find((option) => option.value === folder?.value) ||
      folder ||
      null,
    [folder, folderOptions],
  );

  useEffect(() => {
    if (!folders?.length || !folderOptions.length) {
      return;
    }

    if (
      !folder ||
      !folderOptions.some((option) => option.value === folder.value)
    ) {
      setFolder(getDefaultContactEmailFolderOption(folders));
    }
  }, [folder, folderOptions, folders]);

  useEffect(() => {
    if (!user?.id || !contactEmail || !orgId || !folder?.value) {
      setEmails([]);
      return;
    }

    let isCurrentRequest = true;

    setIsLoadingEmails(true);
    setEmailsError("");

    getContactEmails({
      userId: user.id,
      folder: folder.value,
      pageNumber: undefined,
      contactEmail,
      orgId,
    })
      .unwrap()
      .then((response) => {
        if (!isCurrentRequest) {
          return;
        }

        setEmails(response.data);
      })
      .catch((requestError) => {
        if (!isCurrentRequest) {
          return;
        }

        setEmails([]);
        setEmailsError(getEmailRequestErrorText(requestError));
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoadingEmails(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [contactEmail, folder?.value, getContactEmails, orgId, user?.id]);

  const [connectEmail, { data, isSuccess }] = useGetRedirectUrlMutation();

  useEffect(() => {
    if (isSuccess) {
      const a = document.createElement("a");
      a.setAttribute("href", data);
      a.setAttribute("target", "_blank");
      a.click();
    }
  }, [data, isSuccess]);

  const isConnected =
    emailsError !==
      "Grant for this User doesn't exists. Try authorizing again." &&
    errorFolder !==
      "Grant for this User doesn't exists. Try authorizing again.";

  const folderSelect = (
    <div className={"w-[180px]"}>
      <CustomSelect
        options={folderOptions}
        value={selectedFolderOption}
        onChange={(newValue) => setFolder(newValue as SelectOptionType)}
        selectStyle={{
          emailPage: true,
          fullWidth: true,
          size: "lg",
          listHeight: "180px",
        }}
      />
    </div>
  );

  const handleToggleEmail = (emailId: string) => {
    setExpandedEmailIds((current) => ({
      ...current,
      [emailId]: !current[emailId],
    }));
  };

  const handleToggleAll = () => {
    const allExpanded =
      emails.length > 0 && emails.every((email) => expandedEmailIds[email.id]);

    setExpandedEmailIds(
      allExpanded
        ? {}
        : Object.fromEntries(emails.map((email) => [email.id, true])),
    );
  };

  const handleOpenReply = (email: Email) => {
    setActionEmail(email);
    setIsOpenReply(true);
  };

  const handleOpenReplyAll = (email: Email) => {
    setActionEmail(email);
    setIsOpenReplyAll(true);
  };

  const handleOpenForward = (email: Email) => {
    setActionEmail(email);
    setIsForward(true);
  };

  if (isLoadingFolders) {
    return <LottieLoading />;
  }
  return (
    <div className={"font-Manrope"}>
      {!isConnected ? (
        <EmailConnectionEmptyState onConnect={() => setIsOpenProvider(true)} />
      ) : isLoadingEmails ? (
        <div className={"max-w-[860px]"}>
          <div className={"mb-[18px] flex justify-end"}>{folderSelect}</div>
          <LottieLoading />
        </div>
      ) : emails.length === 0 ? (
        <ContactEmailsNoMessages
          folderSelect={folderSelect}
          onCompose={() => setIsOpenCompose(true)}
          {...(contactEmailsErrorState(emailsError) || {})}
        />
      ) : (
        <ContactEmailsThread
          emails={emails}
          expandedEmailIds={expandedEmailIds}
          folderSelect={folderSelect}
          onToggleEmail={handleToggleEmail}
          onToggleAll={handleToggleAll}
          onCompose={() => setIsOpenCompose(true)}
          onReply={handleOpenReply}
          onReplyAll={handleOpenReplyAll}
          onForward={handleOpenForward}
        />
      )}
      {isOpenCompose && (
        <ComposeEmailModal
          isActive={isOpenCompose}
          contactEmail={[
            {
              email: contactEmail,
              businessContactId: organization?.id || "",
              customerName: organization?.companyName || "",
              companyName: organization?.companyName || "",
              contactPersonFirstName:
                organization?.contactPersons?.firstName || "",
              contactPersonLastName:
                organization?.contactPersons?.lastName || "",
            },
          ]}
          onClose={() => setIsOpenCompose(false)}
        />
      )}
      {isOpenReply && actionEmail && (
        <ComposeEmailModal
          isActive={isOpenReply}
          onClose={() => setIsOpenReply(false)}
          isReply={true}
          email={actionEmail}
          selectedMail={actionEmail}
        />
      )}
      {isOpenReplyAll && actionEmail && (
        <ComposeEmailModal
          isActive={isOpenReplyAll}
          onClose={() => setIsOpenReplyAll(false)}
          isReplyAll={true}
          email={actionEmail}
          selectedMail={actionEmail}
        />
      )}
      {isForward && actionEmail && (
        <ComposeEmailModal
          isActive={isForward}
          isForward
          email={actionEmail}
          onClose={() => setIsForward(false)}
        />
      )}
      {isOpenProvider && (
        <EmailProviderModal
          onClose={() => setIsOpenProvider(false)}
          onConnect={(provider) =>
            connectEmail({
              provider,
              returnUrl: document.URL,
              userId: user?.id || "",
            })
          }
        />
      )}
    </div>
  );
};
